import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  TouchableOpacity, 
  ScrollView, 
  ActivityIndicator, 
  Platform,
  Animated 
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import Modal from './Modal';
import AnimatedButton from './AnimatedButton';
import { V12_CYLINDERS, v12DiagnosticService } from '../services/v12DiagnosticService';

/**
 * V12 Business Diagnostic Popup
 * 
 * Art Deco styled diagnostic questionnaire
 * "Your business firing on all twelve cylinders, like a Lamborghini engine."
 */
const V12DiagnosticPopup = ({ 
  visible, 
  onClose, 
  onSubmit, 
  userId,
  weekNumber = null,
  monthNumber = null,
  isFirstTime = false,
  loading = false,
  previousData = null, // Previous cylinder data to pre-fill
}) => {
  const { palette } = useTheme();
  const [cylinderData, setCylinderData] = useState({});
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-12 = cylinders, 13 = summary
  const [submitting, setSubmitting] = useState(false);
  const [fadeAnim] = useState(new Animated.Value(0));

  // Initialize cylinder data
  useEffect(() => {
    if (visible) {
      const initialData = {};
      Object.keys(V12_CYLINDERS).forEach(key => {
        initialData[key] = previousData?.[key]?.items || [false, false, false];
      });
      setCylinderData(initialData);
      setCurrentStep(0);
      
      // Fade in animation
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, previousData]);

  const handleToggleItem = (cylinderId, itemIndex) => {
    setCylinderData(prev => ({
      ...prev,
      [cylinderId]: prev[cylinderId].map((val, idx) => 
        idx === itemIndex ? !val : val
      ),
    }));
  };

  const getTotalCompleted = () => {
    return Object.values(cylinderData).reduce((total, items) => 
      total + items.filter(Boolean).length, 0
    );
  };

  const getCylindersComplete = () => {
    return Object.values(cylinderData).filter(items => 
      items.every(Boolean)
    ).length;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await v12DiagnosticService.saveDiagnostic(
        userId, 
        cylinderData, 
        weekNumber, 
        monthNumber
      );
      
      if (result.success && onSubmit) {
        onSubmit(result);
      }
    } catch (error) {
      console.error('[V12] Error submitting:', error);
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    setCurrentStep(0);
    if (onClose) onClose();
  };

  const cylinderKeys = Object.keys(V12_CYLINDERS);
  const currentCylinder = currentStep > 0 && currentStep <= 12 
    ? V12_CYLINDERS[cylinderKeys[currentStep - 1]] 
    : null;

  // Art Deco color palette
  const artDecoColors = {
    gold: '#D4AF37',
    goldLight: '#F5E6A3',
    black: '#1A1A1A',
    cream: '#FDF8E8',
    copper: '#B87333',
    teal: '#008080',
    burgundy: '#722F37',
  };

  // Period label
  const getPeriodLabel = () => {
    if (isFirstTime) return 'Initial Diagnostic - Day 1';
    if (weekNumber !== null) return `Week ${weekNumber} Check-In`;
    if (monthNumber !== null) return `Month ${monthNumber} Review`;
    return 'Business Diagnostic';
  };

  if (!visible) return null;

  return (
    <Modal 
      visible={visible} 
      onClose={handleClose}
      showCloseButton={currentStep === 0}
      backgroundColor={artDecoColors.cream}
      maxWidth={900}
      minWidth={750}
      minHeight={650}
    >
      <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
        {/* Art Deco Header */}
        <View style={styles.artDecoHeader}>
          <View style={[styles.artDecoLine, { backgroundColor: artDecoColors.gold }]} />
          <View style={styles.artDecoCenter}>
            <Text style={[styles.v12Badge, { backgroundColor: artDecoColors.gold, color: artDecoColors.black }]}>
              V12
            </Text>
          </View>
          <View style={[styles.artDecoLine, { backgroundColor: artDecoColors.gold }]} />
        </View>

        {currentStep === 0 && (
          /* Introduction Screen */
          <View style={styles.introContainer}>
            <Text style={[styles.mainTitle, { color: artDecoColors.black }]}>
              V12 System
            </Text>
            <Text style={[styles.subtitle, { color: artDecoColors.copper }]}>
              Annual Business Diagnostic
            </Text>
            
            <View style={[styles.quoteBorder, { borderColor: artDecoColors.gold }]}>
              <Text style={[styles.quoteText, { color: artDecoColors.black }]}>
                "I call it the V12 System—your business firing on all twelve cylinders, 
                like a Lamborghini engine. Every cylinder has to be working perfectly."
              </Text>
            </View>

            <Text style={[styles.periodBadge, { backgroundColor: artDecoColors.gold + '30', color: artDecoColors.black }]}>
              {getPeriodLabel()}
            </Text>

            <Text style={[styles.instructionText, { color: artDecoColors.black }]}>
              Check each item you've completed across the 12 critical business cylinders.
              {'\n\n'}
              When all twelve cylinders fire together, your business becomes a machine: 
              {'\n'}powerful, efficient, and unstoppable.
            </Text>

            <View style={styles.cylinderPreview}>
              {cylinderKeys.map((key, idx) => (
                <View 
                  key={key} 
                  style={[
                    styles.cylinderDot,
                    { backgroundColor: artDecoColors.gold + '40', borderColor: artDecoColors.gold }
                  ]}
                >
                  <Text style={[styles.cylinderDotText, { color: artDecoColors.black }]}>
                    {V12_CYLINDERS[key].emoji}
                  </Text>
                </View>
              ))}
            </View>

            <AnimatedButton
              title="Begin Diagnostic →"
              onPress={() => setCurrentStep(1)}
              style={[styles.primaryButton, { backgroundColor: artDecoColors.gold }]}
              textStyle={[styles.primaryButtonText, { color: artDecoColors.black }]}
            />
          </View>
        )}

        {currentStep >= 1 && currentStep <= 12 && currentCylinder && (
          /* Cylinder Checklist Screen */
          <View style={styles.cylinderContainer}>
            <View style={styles.progressHeader}>
              <Text style={[styles.progressText, { color: artDecoColors.copper }]}>
                Cylinder {currentStep} of 12
              </Text>
              <View style={[styles.progressBar, { backgroundColor: artDecoColors.gold + '30' }]}>
                <View 
                  style={[
                    styles.progressFill, 
                    { 
                      backgroundColor: artDecoColors.gold,
                      width: `${(currentStep / 12) * 100}%` 
                    }
                  ]} 
                />
              </View>
            </View>

            <View style={styles.cylinderHeader}>
              <View style={[styles.cylinderIcon, { backgroundColor: artDecoColors.gold + '20', borderColor: artDecoColors.gold }]}>
                <Text style={styles.cylinderEmoji}>{currentCylinder.emoji}</Text>
              </View>
              <View style={styles.cylinderTitleContainer}>
                <Text style={[styles.cylinderNumber, { color: artDecoColors.copper }]}>
                  Cylinder {currentCylinder.number}
                </Text>
                <Text style={[styles.cylinderTitle, { color: artDecoColors.black }]}>
                  {currentCylinder.title}
                </Text>
              </View>
            </View>

            <View style={styles.checklistContainer}>
              {currentCylinder.items.map((item, idx) => {
                const isChecked = cylinderData[currentCylinder.id]?.[idx] || false;
                return (
                  <TouchableOpacity
                    key={idx}
                    style={[
                      styles.checklistItem,
                      { 
                        borderColor: isChecked ? artDecoColors.gold : artDecoColors.gold + '40',
                        backgroundColor: isChecked ? artDecoColors.gold + '15' : 'transparent',
                      }
                    ]}
                    onPress={() => handleToggleItem(currentCylinder.id, idx)}
                  >
                    <View style={[
                      styles.checkbox,
                      { 
                        borderColor: isChecked ? artDecoColors.gold : artDecoColors.copper + '60',
                        backgroundColor: isChecked ? artDecoColors.gold : 'transparent',
                      }
                    ]}>
                      {isChecked && (
                        <Text style={[styles.checkmark, { color: artDecoColors.black }]}>✓</Text>
                      )}
                    </View>
                    <Text style={[
                      styles.checklistText,
                      { color: artDecoColors.black },
                      isChecked && styles.checklistTextChecked,
                    ]}>
                      {item}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={styles.navigationButtons}>
              <TouchableOpacity
                style={[styles.navButton, { borderColor: artDecoColors.gold }]}
                onPress={() => setCurrentStep(prev => prev - 1)}
              >
                <Text style={[styles.navButtonText, { color: artDecoColors.gold }]}>
                  ← Previous
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.navButton, styles.navButtonPrimary, { backgroundColor: artDecoColors.gold }]}
                onPress={() => setCurrentStep(prev => prev + 1)}
              >
                <Text style={[styles.navButtonText, { color: artDecoColors.black }]}>
                  {currentStep === 12 ? 'Review →' : 'Next →'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {currentStep === 13 && (
          /* Summary Screen */
          <ScrollView style={styles.summaryScrollView} showsVerticalScrollIndicator={false}>
            <View style={styles.summaryContainer}>
              <Text style={[styles.summaryTitle, { color: artDecoColors.black }]}>
                Diagnostic Complete
              </Text>
              
              {/* Score Display */}
              <View style={[styles.scoreCard, { backgroundColor: artDecoColors.gold + '15', borderColor: artDecoColors.gold }]}>
                <View style={styles.scoreRow}>
                  <View style={styles.scoreItem}>
                    <Text style={[styles.scoreNumber, { color: artDecoColors.gold }]}>
                      {getCylindersComplete()}
                    </Text>
                    <Text style={[styles.scoreLabel, { color: artDecoColors.black }]}>
                      /12 Cylinders{'\n'}Firing
                    </Text>
                  </View>
                  <View style={[styles.scoreDivider, { backgroundColor: artDecoColors.gold + '40' }]} />
                  <View style={styles.scoreItem}>
                    <Text style={[styles.scoreNumber, { color: artDecoColors.gold }]}>
                      {getTotalCompleted()}
                    </Text>
                    <Text style={[styles.scoreLabel, { color: artDecoColors.black }]}>
                      /36 Items{'\n'}Completed
                    </Text>
                  </View>
                </View>
              </View>

              {/* Cylinder Summary Grid */}
              <Text style={[styles.summarySectionTitle, { color: artDecoColors.black }]}>
                Your V12 Engine Status
              </Text>
              
              <View style={styles.cylinderGrid}>
                {cylinderKeys.map(key => {
                  const cylinder = V12_CYLINDERS[key];
                  const items = cylinderData[key] || [false, false, false];
                  const completed = items.filter(Boolean).length;
                  const isComplete = completed === 3;
                  
                  return (
                    <View 
                      key={key}
                      style={[
                        styles.cylinderSummaryCard,
                        { 
                          borderColor: isComplete ? artDecoColors.gold : artDecoColors.gold + '40',
                          backgroundColor: isComplete ? artDecoColors.gold + '15' : 'transparent',
                        }
                      ]}
                    >
                      <Text style={styles.cylinderSummaryEmoji}>{cylinder.emoji}</Text>
                      <Text 
                        style={[styles.cylinderSummaryTitle, { color: artDecoColors.black }]}
                        numberOfLines={2}
                      >
                        {cylinder.title}
                      </Text>
                      <View style={[styles.cylinderProgress, { backgroundColor: artDecoColors.gold + '30' }]}>
                        <View 
                          style={[
                            styles.cylinderProgressFill,
                            { 
                              backgroundColor: isComplete ? artDecoColors.gold : artDecoColors.copper,
                              width: `${(completed / 3) * 100}%`,
                            }
                          ]} 
                        />
                      </View>
                      <Text style={[styles.cylinderProgressText, { color: artDecoColors.copper }]}>
                        {completed}/3
                      </Text>
                    </View>
                  );
                })}
              </View>

              {/* Motivational Message */}
              <View style={[styles.motivationCard, { borderColor: artDecoColors.gold }]}>
                {getCylindersComplete() === 12 ? (
                  <Text style={[styles.motivationText, { color: artDecoColors.black }]}>
                    🏆 All 12 cylinders firing! Your business is running like a 
                    Lamborghini engine. Keep up the excellence!
                  </Text>
                ) : getCylindersComplete() >= 8 ? (
                  <Text style={[styles.motivationText, { color: artDecoColors.black }]}>
                    🔥 Excellent progress! {getCylindersComplete()} cylinders firing. 
                    You're well on your way to building an unstoppable machine.
                  </Text>
                ) : getCylindersComplete() >= 4 ? (
                  <Text style={[styles.motivationText, { color: artDecoColors.black }]}>
                    💪 Good foundation! Focus on the cylinders that need attention 
                    to increase your business power.
                  </Text>
                ) : (
                  <Text style={[styles.motivationText, { color: artDecoColors.black }]}>
                    🌱 Every journey starts with a single step. Identify 1-2 cylinders 
                    to focus on this week and build momentum.
                  </Text>
                )}
              </View>

              <View style={styles.summaryActions}>
                <TouchableOpacity
                  style={[styles.navButton, { borderColor: artDecoColors.gold }]}
                  onPress={() => setCurrentStep(12)}
                >
                  <Text style={[styles.navButtonText, { color: artDecoColors.gold }]}>
                    ← Back to Edit
                  </Text>
                </TouchableOpacity>
                
                <AnimatedButton
                  title={submitting ? 'Saving...' : 'Save Diagnostic'}
                  onPress={handleSubmit}
                  loading={submitting}
                  disabled={submitting}
                  style={[styles.submitButton, { backgroundColor: artDecoColors.gold }]}
                  textStyle={[styles.submitButtonText, { color: artDecoColors.black }]}
                />
              </View>
            </View>
          </ScrollView>
        )}

        {/* Art Deco Footer */}
        <View style={styles.artDecoFooter}>
          <View style={[styles.artDecoLineSmall, { backgroundColor: artDecoColors.gold }]} />
          <View style={[styles.artDecoDiamond, { backgroundColor: artDecoColors.gold }]} />
          <View style={[styles.artDecoLineSmall, { backgroundColor: artDecoColors.gold }]} />
        </View>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 850,
    minWidth: 700,
    minHeight: 600,
    alignSelf: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  
  // Art Deco Header
  artDecoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
    paddingHorizontal: 20,
  },
  artDecoLine: {
    flex: 1,
    height: 2,
  },
  artDecoCenter: {
    paddingHorizontal: 16,
  },
  v12Badge: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '900',
    letterSpacing: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  
  // Introduction
  introContainer: {
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 16,
  },
  mainTitle: {
    fontSize: 40,
    fontFamily: 'Archivo',
    fontWeight: '900',
    letterSpacing: 2,
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 3,
    textAlign: 'center',
    marginBottom: 28,
  },
  quoteBorder: {
    borderWidth: 2,
    borderStyle: 'solid',
    padding: 24,
    marginBottom: 28,
    maxWidth: 550,
    width: '100%',
  },
  quoteText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    textAlign: 'center',
    lineHeight: 24,
  },
  periodBadge: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 4,
    marginBottom: 24,
    letterSpacing: 1,
  },
  instructionText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: 28,
    maxWidth: 500,
    paddingHorizontal: 16,
  },
  cylinderPreview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 36,
    paddingHorizontal: 16,
  },
  cylinderDot: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cylinderDotText: {
    fontSize: 18,
  },
  primaryButton: {
    paddingHorizontal: 48,
    paddingVertical: 18,
    borderRadius: 4,
    minWidth: 240,
  },
  primaryButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
    letterSpacing: 1,
    textAlign: 'center',
  },
  
  // Cylinder Screen
  cylinderContainer: {
    paddingHorizontal: 20,
  },
  progressHeader: {
    marginBottom: 24,
  },
  progressText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  cylinderHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    gap: 16,
  },
  cylinderIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cylinderEmoji: {
    fontSize: 28,
  },
  cylinderTitleContainer: {
    flex: 1,
  },
  cylinderNumber: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  cylinderTitle: {
    fontSize: 22,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  checklistContainer: {
    gap: 12,
    marginBottom: 32,
  },
  checklistItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    borderWidth: 2,
    gap: 16,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  checkbox: {
    width: 28,
    height: 28,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    fontSize: 16,
    fontWeight: '700',
  },
  checklistText: {
    flex: 1,
    fontSize: 15,
    fontFamily: 'Archivo',
    lineHeight: 22,
  },
  checklistTextChecked: {
    fontWeight: '600',
  },
  navigationButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
  },
  navButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  navButtonPrimary: {
    borderWidth: 0,
  },
  navButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  
  // Summary Screen
  summaryScrollView: {
    maxHeight: 500,
  },
  summaryContainer: {
    paddingHorizontal: 20,
  },
  summaryTitle: {
    fontSize: 28,
    fontFamily: 'Archivo',
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 24,
    letterSpacing: 1,
  },
  scoreCard: {
    borderWidth: 2,
    borderRadius: 8,
    padding: 24,
    marginBottom: 32,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreItem: {
    flex: 1,
    alignItems: 'center',
  },
  scoreNumber: {
    fontSize: 48,
    fontFamily: 'Archivo',
    fontWeight: '900',
  },
  scoreLabel: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '500',
    textAlign: 'center',
    marginTop: 4,
  },
  scoreDivider: {
    width: 2,
    height: 60,
    marginHorizontal: 24,
  },
  summarySectionTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
  },
  cylinderGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
    justifyContent: 'center',
  },
  cylinderSummaryCard: {
    width: 100,
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    alignItems: 'center',
  },
  cylinderSummaryEmoji: {
    fontSize: 24,
    marginBottom: 6,
  },
  cylinderSummaryTitle: {
    fontSize: 10,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
    height: 28,
  },
  cylinderProgress: {
    width: '100%',
    height: 4,
    borderRadius: 2,
    marginBottom: 4,
  },
  cylinderProgressFill: {
    height: '100%',
    borderRadius: 2,
  },
  cylinderProgressText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  motivationCard: {
    borderWidth: 2,
    borderStyle: 'solid',
    padding: 20,
    marginBottom: 24,
    borderRadius: 8,
  },
  motivationText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 24,
  },
  summaryActions: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  submitButton: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  
  // Art Deco Footer
  artDecoFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 32,
    paddingHorizontal: 20,
    paddingBottom: 8,
  },
  artDecoLineSmall: {
    flex: 1,
    height: 2,
    maxWidth: 100,
  },
  artDecoDiamond: {
    width: 12,
    height: 12,
    transform: [{ rotate: '45deg' }],
    marginHorizontal: 12,
  },
});

export default V12DiagnosticPopup;

